import React from 'react';
import {
  Grid,
  Typography,
  Paper,
  Box,
  Chip
} from '@mui/material';

const WeatherDataDisplay = ({ 
  weatherData, 
  title = "Weather Conditions",
  compact = false 
}) => {
  const hasStandardizedData = weatherData && (
    (weatherData.snow_temperature_c !== null && weatherData.snow_temperature_c !== undefined) ||
    (weatherData.air_temperature_c !== null && weatherData.air_temperature_c !== undefined) ||
    (weatherData.snow_humidity_doser !== null && weatherData.snow_humidity_doser !== undefined) ||
    (weatherData.air_humidity_rh !== null && weatherData.air_humidity_rh !== undefined) ||
    (weatherData.clouds_sky_parts !== null && weatherData.clouds_sky_parts !== undefined) ||
    weatherData.visibility ||
    weatherData.wind ||
    weatherData.precipitation ||
    weatherData.artificial_snow ||
    weatherData.natural_snow ||
    weatherData.grain_size ||
    weatherData.snow_humidity ||
    weatherData.track_hardness ||
    weatherData.track_shininess ||
    weatherData.track_dirtiness
  );

  if (!hasStandardizedData) {
    return null;
  }

  const formatValue = (value, unit = '') => {
    if (value === null || value === undefined || value === '') return null;
    return `${value}${unit}`;
  };

  const getVisibilityLabel = (value) => {
    const labels = {
      'unlimited': 'Unlimited',
      '10km': '10 km',
      '5km': '5 km',
      '2km': '2 km',
      '1km': '1 km',
      '500m': '500 m',
      '200m': '200 m',
      '100m': '100 m',
      '50m': '50 m',
      '10m': '10 m',
      '5m': '5 m',
      '1m': '1 m'
    };
    return labels[value] || value;
  };

  const getWindLabel = (value) => {
    const labels = {
      'calm': 'Calm',
      'light': 'Light Breeze',
      'moderate': 'Moderate Breeze',
      'fresh': 'Fresh Breeze',
      'strong': 'Strong Breeze',
      'gale': 'Gale',
      'storm': 'Storm',
      'extreme': 'Extreme'
    };
    return labels[value] || value;
  };

  const getPrecipitationLabel = (value) => {
    const labels = {
      'none': 'None',
      'snow': 'Snow',
      'light_snow': 'Light Snow',
      'heavy_snow': 'Heavy Snow',
      'rain': 'Rain',
      'light_rain': 'Light Rain',
      'heavy_rain': 'Heavy Rain',
      'sleet': 'Sleet',
      'hail': 'Hail'
    };
    return labels[value] || value;
  };

  const getSnowLabel = (type, value) => {
    const labels = {
      artificial: {
        'A1': 'A1 (Falling new)',
        'A2': 'A2 (New)',
        'A3': 'A3 (Irregularly decomposed new snow)',
        'A4': 'A4 (Irregularly decomposed transformed snow)',
        'A5': 'A5 (Transformed)'
      },
      natural: {
        'FN': 'FN (Falling New)',
        'NS': 'NS (New)',
        'IN': 'IN (Irregularly decomposed new)',
        'IT': 'IT (Irregularly decomposed transformed snow)',
        'TR': 'TR (Transformed)'
      },
      grain: {
        'G0': 'G0 (0.0-0.2mm - Extra fine)',
        'G1': 'G1 (0.2-0.5mm - Very fine)',
        'G2': 'G2 (0.5-1.0mm - Fine)',
        'G3': 'G3 (2.0-4.0mm - Coarse)',
        'G5': 'G5 (>4.0mm - Very coarse)'
      },
      humidity: {
        'DS': 'DS (Dry)',
        'W1': 'W1 (Moist)',
        'W2': 'W2 (Wet)',
        'W3': 'W3 (Very wet)',
        'W4': 'W4 (Slush)'
      },
      hardness: {
        'H1': 'H1 (Very soft)',
        'H2': 'H2 (Soft)',
        'H3': 'H3 (Medium hard)',
        'H4': 'H4 (Hard)',
        'H5': 'H5 (Very hard)',
        'H6': 'H6 (Ice)'
      },
      shininess: {
        'T0': 'T0 (Matte)',
        'T1': 'T1 (Partly shiny)',
        'T2': 'T2 (Shiny)'
      },
      dirtiness: {
        'D0': 'D0 (Clean)',
        'D1': 'D1 (Partly dirty)',
        'D2': 'D2 (Dirty)'
      }
    };
    return labels[type]?.[value] || value;
  };

  return (
    <Paper sx={{ p: compact ? 2 : 3, mb: 2 }}>
      <Typography variant="h6" gutterBottom>
        {title}
      </Typography>

      <Grid container spacing={3}>
        {/* Temperature Section */}
        {(weatherData.snow_temperature_c !== null && weatherData.snow_temperature_c !== undefined) ||
         (weatherData.air_temperature_c !== null && weatherData.air_temperature_c !== undefined) ? (
          <Grid item xs={12}>
            <Box sx={{ p: 2, border: '1px solid #e0e0e0', borderRadius: 1, mb: 2 }}>
              <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600, color: 'primary.main' }}>
                Temperature
              </Typography>
              <Grid container spacing={1}>
                {formatValue(weatherData.snow_temperature_c, '°C') && (
                  <Grid item>
                    <Chip label={`Snow: ${formatValue(weatherData.snow_temperature_c, '°C')}`} size="small" />
                  </Grid>
                )}
                {formatValue(weatherData.air_temperature_c, '°C') && (
                  <Grid item>
                    <Chip label={`Air: ${formatValue(weatherData.air_temperature_c, '°C')}`} size="small" />
                  </Grid>
                )}
              </Grid>
            </Box>
          </Grid>
        ) : null}

        {/* Humidity Section */}
        {(weatherData.snow_humidity_doser !== null && weatherData.snow_humidity_doser !== undefined) ||
         (weatherData.air_humidity_rh !== null && weatherData.air_humidity_rh !== undefined) ? (
          <Grid item xs={12}>
            <Box sx={{ p: 2, border: '1px solid #e0e0e0', borderRadius: 1, mb: 2 }}>
              <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600, color: 'primary.main' }}>
                Humidity
              </Typography>
              <Grid container spacing={1}>
                {formatValue(weatherData.snow_humidity_doser) && (
                  <Grid item>
                    <Chip label={`Snow: ${formatValue(weatherData.snow_humidity_doser)}`} size="small" />
                  </Grid>
                )}
                {formatValue(weatherData.air_humidity_rh, '% rH') && (
                  <Grid item>
                    <Chip label={`Air: ${formatValue(weatherData.air_humidity_rh, '% rH')}`} size="small" />
                  </Grid>
                )}
              </Grid>
            </Box>
          </Grid>
        ) : null}

        {/* Atmospheric Conditions Section */}
        {(weatherData.clouds_sky_parts !== null && weatherData.clouds_sky_parts !== undefined) ||
         weatherData.visibility || weatherData.wind || weatherData.precipitation ? (
          <Grid item xs={12}>
            <Box sx={{ p: 2, border: '1px solid #e0e0e0', borderRadius: 1, mb: 2 }}>
              <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600, color: 'primary.main' }}>
                Atmospheric Conditions
              </Typography>
              <Grid container spacing={1}>
                {formatValue(weatherData.clouds_sky_parts, '/10') && (
                  <Grid item>
                    <Chip label={`Clouds: ${formatValue(weatherData.clouds_sky_parts, '/10')}`} size="small" />
                  </Grid>
                )}
                {weatherData.visibility && (
                  <Grid item>
                    <Chip label={`Visibility: ${getVisibilityLabel(weatherData.visibility)}`} size="small" />
                  </Grid>
                )}
                {weatherData.wind && (
                  <Grid item>
                    <Chip label={`Wind: ${getWindLabel(weatherData.wind)}`} size="small" />
                  </Grid>
                )}
                {weatherData.precipitation && (
                  <Grid item>
                    <Chip label={`Precipitation: ${getPrecipitationLabel(weatherData.precipitation)}`} size="small" />
                  </Grid>
                )}
              </Grid>
            </Box>
          </Grid>
        ) : null}

        {/* Snow Classification Section */}
        {weatherData.artificial_snow || weatherData.natural_snow || weatherData.grain_size || weatherData.snow_humidity ? (
          <Grid item xs={12}>
            <Box sx={{ p: 2, border: '1px solid #e0e0e0', borderRadius: 1, mb: 2 }}>
              <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600, color: 'primary.main' }}>
                Snow Classification
              </Typography>
              <Grid container spacing={1}>
                {weatherData.artificial_snow && (
                  <Grid item>
                    <Chip label={getSnowLabel('artificial', weatherData.artificial_snow)} size="small" />
                  </Grid>
                )}
                {weatherData.natural_snow && (
                  <Grid item>
                    <Chip label={getSnowLabel('natural', weatherData.natural_snow)} size="small" />
                  </Grid>
                )}
                {weatherData.grain_size && (
                  <Grid item>
                    <Chip label={getSnowLabel('grain', weatherData.grain_size)} size="small" />
                  </Grid>
                )}
                {weatherData.snow_humidity && (
                  <Grid item>
                    <Chip label={getSnowLabel('humidity', weatherData.snow_humidity)} size="small" />
                  </Grid>
                )}
              </Grid>
            </Box>
          </Grid>
        ) : null}

        {/* Track Conditions Section */}
        {weatherData.track_hardness || weatherData.track_shininess || weatherData.track_dirtiness ? (
          <Grid item xs={12}>
            <Box sx={{ p: 2, border: '1px solid #e0e0e0', borderRadius: 1, mb: 2 }}>
              <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600, color: 'primary.main' }}>
                Track Conditions
              </Typography>
              <Grid container spacing={1}>
                {weatherData.track_hardness && (
                  <Grid item>
                    <Chip label={getSnowLabel('hardness', weatherData.track_hardness)} size="small" />
                  </Grid>
                )}
                {weatherData.track_shininess && (
                  <Grid item>
                    <Chip label={getSnowLabel('shininess', weatherData.track_shininess)} size="small" />
                  </Grid>
                )}
                {weatherData.track_dirtiness && (
                  <Grid item>
                    <Chip label={getSnowLabel('dirtiness', weatherData.track_dirtiness)} size="small" />
                  </Grid>
                )}
              </Grid>
            </Box>
          </Grid>
        ) : null}
      </Grid>
    </Paper>
  );
};

export default WeatherDataDisplay;